import { Observable } from "rxjs";
import {
    BRAND_AVAILABLE_SHIPPING_CARRIERS_COMPLETE,
    BRAND_AVAILABLE_SHIPPING_CARRIERS_REQUEST,
    BRAND_SHIPPING_CARRIER_COMPLETE,
    BRAND_SHIPPING_CARRIER_REQUEST,
    SHIPPING_ACCOUNT_CARRIER_CREATE,
    SHIPPING_ACCOUNT_CARRIER_CREATE_COMPLETE,
    SHIPPING_ACCOUNT_CARRIER_DELETE,
    SHIPPING_ACCOUNT_CARRIER_DELETE_COMPLETE,
    SHIPPING_ACCOUNT_CARRIER_UPDATE,
    SHIPPING_ACCOUNT_CARRIER_UPDATE_COMPLETE,
    SHIPPING_ACCOUNT_CARRIERS_REQUEST,
    SHIPPING_CARRIER_SERVICES_COMPLETE,
    SHIPPING_CARRIER_SERVICES_REQUEST,
} from "constants/action-types";
import siteConfig from "site-config";
import { formatMessage, format } from "lib/globalize-util";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import { add as snackbarAdd } from "actions/snackbars";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import {
    AccountCarriers as AccountCarriersSchema,
    CarrierServiceList as CarrierServiceListsSchema,
    ShippingCarriers as ShippingCarriersSchema,
} from "schemas/shipping-account";
import { updateEntities } from "redux-query";
import type { LoadWorkingOrderAction } from "actions/working-order-state";

const updateShippingCarriers = (
    meta: { loadCart: LoadWorkingOrderAction["payload"] },
    payload: { payload?: string[]; error?: boolean; errorObject?: any; data?: object[] }
) => ({
    type: BRAND_SHIPPING_CARRIER_COMPLETE,
    meta,
    ...payload,
});

export const fetchAvailableCarriers = (action$, store) => {
    return action$.ofType(BRAND_AVAILABLE_SHIPPING_CARRIERS_REQUEST).concatMap(() => {
        return ajax({
            method: "GET",
            url: `${siteConfig.api}/api/v3.0/shipping/available-carriers`,
        })
            .map(({ response, status }) => {
                if (response && status === 200) {
                    const carrierServicesRecords = getRecordsFromSchema(ShippingCarriersSchema);
                    const carrierServiceData = normalizeData(response, ShippingCarriersSchema, carrierServicesRecords);
                    return { entities: carrierServiceData.entities, result: carrierServiceData.result, error: false };
                } else {
                    return { error: true, entities: null, result: [] };
                }
            })
            .flatMap(({ entities, result, error }) => {
                if (error) {
                    return [
                        store.dispatch(snackbarAdd({ message: "Error loading shipping carriers" })),
                        {
                            payload: [],
                            error: true,
                            type: BRAND_AVAILABLE_SHIPPING_CARRIERS_COMPLETE,
                        },
                    ];
                } else {
                    return [
                        updateEntities({
                            shippingCarriers: (shippingCarriers = {}) => {
                                return {
                                    ...entities.shippingCarrier,
                                };
                            },
                        }),
                        {
                            payload: result,
                            type: BRAND_AVAILABLE_SHIPPING_CARRIERS_COMPLETE,
                        },
                    ];
                }
            });
    });
};

export const fetchAccountCarrier = (action$, store) => {
    return action$
        .ofType(BRAND_SHIPPING_CARRIER_REQUEST)
        .map((action) => {
            return action.payload;
        })
        .concatMap((carrierId) => {
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/api/v3.0/shipping/carrier/${carrierId}`,
            })
                .map(({ response, status }) => {
                    if (response && status === 200) {
                        const accountCarrierRecords = getRecordsFromSchema(AccountCarriersSchema);
                        const accountCarrierData = normalizeData(
                            response,
                            AccountCarriersSchema,
                            accountCarrierRecords
                        );
                        return {
                            entities: accountCarrierData.entities,
                            result: accountCarrierData.result,
                            error: false,
                        };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ error, entities, result }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error fetching carrier" })),
                            {
                                payload: [],
                                error: true,
                                errorObject: error,
                                type: BRAND_SHIPPING_CARRIER_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                accountCarrier: (accountCarrier = {}) => {
                                    return {
                                        ...entities.accountCarrier,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: BRAND_SHIPPING_CARRIER_COMPLETE,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    let message = formatMessage("There was an error fetching your carrier");
                    if (error.status === 404) {
                        message = formatMessage("Carrier not found");
                    }
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message,
                            })
                        ),
                        Observable.of({
                            error: true,
                            payload: error,
                            errorObject: error,
                            type: BRAND_SHIPPING_CARRIER_COMPLETE,
                        })
                    );
                });
        });
};
export const fetchAccountCarriers = (action$, store) =>
    action$.ofType(SHIPPING_ACCOUNT_CARRIERS_REQUEST).concatMap((action) =>
        ajax({
            method: "GET",
            url: `${siteConfig.api}/api/v3.0/shipping/carriers`,
        })
            .map(({ response, status }) => {
                if (response && status === 200) {
                    const accountCarrierRecords = getRecordsFromSchema(AccountCarriersSchema);
                    const accountCarrierData = normalizeData(response, AccountCarriersSchema, accountCarrierRecords);
                    return {
                        entities: accountCarrierData.entities,
                        result: accountCarrierData.result,
                        data: response,
                        error: false,
                    };
                } else {
                    return { error: true, entities: null, result: [], data: response };
                }
            })
            .flatMap(({ error, entities, result, data }) => {
                if (error) {
                    return [
                        store.dispatch(snackbarAdd({ message: format("Error fetching carrier") })),
                        updateShippingCarriers(action.meta, {
                            payload: [],
                            error: true,
                            errorObject: error,
                            data,
                        }),
                    ];
                } else {
                    return [
                        updateEntities({
                            accountCarrier: (accountCarrier = {}) => {
                                return {
                                    ...entities.accountCarrier,
                                };
                            },
                        }),
                        updateShippingCarriers(action.meta, {
                            payload: result,
                            data,
                        }),
                    ];
                }
            })
            .catch((error) => {
                const message = formatMessage("There was an error fetching your carriers");
                if (error && error.status === 404) {
                    // Slightly weird, but this is a 404 because they don't have a shipping account setup with
                    // ship engine, so while technically a 404, we treat it as empty results
                    return Observable.of(
                        updateShippingCarriers(action?.meta, {
                            payload: [],
                            data: [],
                        })
                    );
                } else {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message,
                            })
                        ),
                        Observable.of(
                            updateShippingCarriers(action?.meta, {
                                error: true,
                                payload: error,
                                errorObject: error,
                                data: [],
                            })
                        )
                    );
                }
            })
    );

export const createAccountCarrier = (action$, store) => {
    return action$
        .ofType(SHIPPING_ACCOUNT_CARRIER_CREATE)
        .map((action) => {
            return action.payload;
        })
        .concatMap((carrierData) => {
            return ajax({
                method: "POST",
                body: carrierData,
                url: `${siteConfig.api}/api/v3.0/shipping/carriers`,
            })
                .map(({ status, response }) => {
                    if (response && status === 201) {
                        const accountCarrierRecords = getRecordsFromSchema(AccountCarriersSchema);
                        const accountCarrierData = normalizeData(
                            response,
                            AccountCarriersSchema,
                            accountCarrierRecords
                        );
                        return {
                            entities: accountCarrierData.entities,
                            result: accountCarrierData.result,
                            error: false,
                        };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ error, entities, result }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error connecting carrier" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_ACCOUNT_CARRIER_CREATE_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                accountCarrier: (accountCarrier = {}) => {
                                    return {
                                        ...entities.accountCarrier,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                error: false,
                                type: SHIPPING_ACCOUNT_CARRIER_CREATE_COMPLETE,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    let message = formatMessage("There was an error connecting your carrier");
                    if (error.status === 400) {
                        if (error.xhr.response.args.errors && error.xhr.response.args.errors.length) {
                            message = error.xhr.response.args.errors[0].message;
                        } else {
                            message = formatMessage(
                                "Your carrier cannot be connected since you are missing required fields"
                            );
                        }
                    } else if (error.status === 403) {
                        message = formatMessage("You do not have permission to modify carrier accounts");
                    } else if (error.status === 409) {
                        message = formatMessage("A carrier for this currency code already exists");
                    } else if (error.status === 302 && error.xhr.response.args?.auth_url) {
                        return Observable.of({
                            error: true,
                            errorObject: error.xhr.response.args,
                            payload: [],
                            type: SHIPPING_ACCOUNT_CARRIER_CREATE_COMPLETE,
                        });
                    } else if (error.status === 404) {
                        return Observable.of({
                            error: true,
                            errorObject: { isAccountNotReady: true },
                            payload: [],
                            type: SHIPPING_ACCOUNT_CARRIER_CREATE_COMPLETE,
                        });
                    }
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message,
                            })
                        ),
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SHIPPING_ACCOUNT_CARRIER_CREATE_COMPLETE,
                        })
                    );
                });
        });
};

export const updateAccountCarrierEpic = (action$, store) => {
    return action$
        .ofType(SHIPPING_ACCOUNT_CARRIER_UPDATE)
        .map((action) => {
            return action.payload;
        })
        .concatMap(({ carrierId, updates }) => {
            return ajax({
                method: "PATCH",
                body: updates,
                url: `${siteConfig.api}/api/v3.0/shipping/carrier/${carrierId}`,
            })
                .map(({ status, response }) => {
                    if (response && status === 202) {
                        const { entities } = store.getState();
                        const accountCarrierRecords = getRecordsFromSchema(AccountCarriersSchema);
                        const accountCarrierData = normalizeData(
                            response,
                            AccountCarriersSchema,
                            accountCarrierRecords
                        );
                        return {
                            entities: accountCarrierData.entities,
                            result: accountCarrierData.result,
                            error: false,
                        };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ error, entities, result }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error updating carrier" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_ACCOUNT_CARRIER_UPDATE_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                accountCarrier: (accountCarrier = {}) => {
                                    return {
                                        ...entities.accountCarrier,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: SHIPPING_ACCOUNT_CARRIER_UPDATE_COMPLETE,
                            },
                            store.dispatch(snackbarAdd({ message: "Account carrier updated" })),
                        ];
                    }
                })
                .catch((error) => {
                    let message = formatMessage("There was an error updating your carrier");
                    if (error.status === 400) {
                        if (error.xhr.response.args.errors && error.xhr.response.args.errors.length) {
                            message = error.xhr.response.args.errors[0].message;
                        } else {
                            message = formatMessage(
                                "Your carrier cannot be connected since you are missing required fields"
                            );
                        }
                    } else if (error.status === 403) {
                        message = formatMessage("You do not have permission to modify carrier accounts");
                    }
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message,
                            })
                        ),
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SHIPPING_ACCOUNT_CARRIER_CREATE_COMPLETE,
                        })
                    );
                });
        });
};

export const deleteAccountCarrier = (action$, store) => {
    return action$
        .ofType(SHIPPING_ACCOUNT_CARRIER_DELETE)
        .map((action) => {
            return action.payload;
        })
        .concatMap((carrierId) => {
            return ajax({
                method: "DELETE",
                url: `${siteConfig.api}/api/v3.0/shipping/carrier/${carrierId}`,
            })
                .map(({ status, response }) => {
                    if (response && [200, 202].includes(status)) {
                        const { entities, shippingAccountCarriers } = store.getState();
                        delete entities.accountCarrier[carrierId];
                        return {
                            entities,
                            result: carrierId,
                            error: false,
                        };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ error, entities, result }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error fetching carrier" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_ACCOUNT_CARRIER_DELETE_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            store.dispatch(snackbarAdd({ message: formatMessage("Carrier deleted") })),
                            updateEntities({
                                accountCarrier: (accountCarrier = {}) => {
                                    return {
                                        ...entities.accountCarrier,
                                    };
                                },
                            }),
                            {
                                payload: result,
                                type: SHIPPING_ACCOUNT_CARRIER_DELETE_COMPLETE,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    let message = formatMessage("There was an error deleting your carrier");
                    if (error.status === 403) {
                        message = formatMessage("You do not have permission to modify carrier accounts");
                    } else if (error.status === 404) {
                        message = formatMessage("Carrier not found");
                    }
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message,
                            })
                        ),
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SHIPPING_ACCOUNT_CARRIER_DELETE_COMPLETE,
                        })
                    );
                });
        });
};

export const fetchCarrierServices = (action$, store) => {
    return action$
        .ofType(SHIPPING_CARRIER_SERVICES_REQUEST)
        .map((action) => {
            return action.payload;
        })
        .concatMap((carrierId) => {
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/api/v3.0/shipping/carrier/${carrierId}/services`,
            })
                .map(({ status, response }) => {
                    if (response && status === 200) {
                        const records = getRecordsFromSchema(CarrierServiceListsSchema);
                        const serviceData = normalizeData(response.services, CarrierServiceListsSchema, records);
                        return { entities: serviceData.entities, result: serviceData.result, error: false };
                    } else {
                        return { error: true, entities: null, result: [] };
                    }
                })
                .flatMap(({ error, entities, result }) => {
                    if (error) {
                        return [
                            store.dispatch(snackbarAdd({ message: "Error fetching shipping services" })),
                            {
                                payload: [],
                                error: true,
                                type: SHIPPING_CARRIER_SERVICES_COMPLETE,
                            },
                        ];
                    } else {
                        return [
                            updateEntities({
                                carrierService: (carrierService = {}) => {
                                    return {
                                        ...entities.carrierServiceList,
                                    };
                                },
                            }),
                            {
                                payload: [result],
                                type: SHIPPING_CARRIER_SERVICES_COMPLETE,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    let message = formatMessage("There was an error fetching carrier services");
                    if (error.status === 403) {
                        message = formatMessage("You do not have permission to view carrier services");
                    } else if (error.status === 404) {
                        message = formatMessage("Carrier not found");
                    }
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message,
                            })
                        ),
                        Observable.of({
                            error: true,
                            payload: error,
                            type: SHIPPING_CARRIER_SERVICES_COMPLETE,
                        })
                    );
                });
        });
};
